import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  BuildingOffice2Icon,
  ClockIcon,
  MapPinIcon,
} from '@heroicons/react/16/solid';
import { Dialog } from '../../common/Catalyst/dialog';
import { Button } from '../../common/Catalyst/button';
import { CompanyReturn, HoaReturn } from '../../clients/rust-api-client';
import { companyService, hoaService } from '../../clients/ApiClients';
import { apiErrorMessage } from '../../utils/apiErrors';
import { ErrorAlert } from '../../common/Custom/ErrorAlert';
import { PropertyPicker } from '../../violation/linear/PropertyPicker';
import { useUser } from '../../hooks/UserContext';
import { UserType } from '../../constants/UserTypes';
import usStates from '../../utils/addressUtils';
import { formatPhoneNumber } from '../../utils/phoneUtils';
import {
  SPLIT_ZONE_STATES,
  US_TIMEZONES,
  needsTimezoneChoice,
} from '../timezones';
import {
  DESCRIPTION_MAX,
  EMPTY_HOA_DRAFT,
  HoaDraft,
  NAME_MAX,
  buildHoaCreate,
  hoaFieldErrors,
  missingForHoa,
} from './hoaMeta';

/**
 * Create an association the Linear way (#665, after #632): a large name,
 * a free-form description, then the chips and the fields an HOA needs.
 *
 * An HOA has more required fields than anything else created this way
 * (street, city, state, ZIP, a contact phone and email), so they are
 * labelled and always shown rather than tucked behind "More". The state
 * and the timezone are chips, the same picker as everywhere else. The
 * timezone defaults to the state's, and must be chosen for a state that
 * spans two zones (#599).
 *
 * The company is the creator's own. Only a SuperAdmin, who may create in
 * any company, gets to choose it.
 */
interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (hoa: HoaReturn) => void;
}

const byName = (companies: CompanyReturn[]) =>
  companies
    .filter((c) => c.is_active !== false)
    .sort((a, b) => a.name.localeCompare(b.name));

export const CreateHoaModal: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const { userType, companyId } = useUser();
  const isSuperAdmin = userType === UserType.SUPER_ADMIN;
  const [draft, setDraft] = useState<HoaDraft>(EMPTY_HOA_DRAFT);
  const [touched, setTouched] = useState<Set<keyof HoaDraft>>(new Set());
  const [companies, setCompanies] = useState<CompanyReturn[]>([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDraft({ ...EMPTY_HOA_DRAFT, companyId: companyId || null });
    setTouched(new Set());
    setError(null);
  }, [open, companyId]);

  // Only a SuperAdmin chooses; everyone else creates in their own company.
  useEffect(() => {
    if (!open || !isSuperAdmin) return;
    let live = true;
    setCompaniesLoading(true);
    companyService
      .getAllCompanies()
      .then((res) => live && setCompanies(byName(res.data)))
      .catch(
        (err) =>
          live && setError(apiErrorMessage(err, "Couldn't load companies."))
      )
      .finally(() => live && setCompaniesLoading(false));
    return () => {
      live = false;
    };
  }, [open, isSuperAdmin]);

  const set =
    <K extends keyof HoaDraft>(key: K) =>
    (value: HoaDraft[K]) =>
      setDraft((d) => ({ ...d, [key]: value }));
  const touch = (key: keyof HoaDraft) => () =>
    setTouched((t) => new Set(t).add(key));

  const missing = missingForHoa(draft);
  const fieldErrors = hoaFieldErrors(draft);
  const shownError = (key: keyof HoaDraft) =>
    touched.has(key) ? fieldErrors[key] : undefined;
  const request = buildHoaCreate(draft);
  const canSubmit = !!request && !saving;
  const company = companies.find((c) => c.id === draft.companyId);
  const state = usStates.find((s) => s.value === draft.state);
  const zone = US_TIMEZONES.find((z) => z.id === draft.timezone);
  const mustChooseZone = needsTimezoneChoice(draft.state);
  const STATE_DEFAULT = '__state__';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!request || saving) return;
    setSaving(true);
    setError(null);
    try {
      const response = await hoaService.createHoa({ hoaCreate: request });
      onCreated(response.data);
      onClose();
    } catch (err) {
      setError(
        apiErrorMessage(err, 'The association was not created. Please try again.')
      );
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full rounded-md border bg-transparent px-2 py-1 text-[13px] text-fg placeholder:text-fg-faint focus:outline-none focus:ring-2 focus:ring-accent';
  const field = (
    key: keyof HoaDraft,
    label: string,
    props: React.InputHTMLAttributes<HTMLInputElement> = {},
    className?: string
  ) => {
    const problem = shownError(key);
    return (
      <label className={clsx('flex flex-col gap-1 text-[12px] text-fg-muted', className)}>
        {label}
        <input
          value={draft[key] ?? ''}
          onChange={(e) => set(key)(e.target.value as never)}
          onBlur={touch(key)}
          aria-invalid={!!problem}
          className={clsx(inputClass, problem ? 'border-danger' : 'border-line')}
          {...props}
        />
        {problem && <span className="text-danger">{problem}</span>}
      </label>
    );
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} size="2xl">
      <form onSubmit={submit} aria-label="New association">
        <p className="text-[12px] font-medium text-fg-muted">New association</p>

        <input
          autoFocus
          value={draft.name}
          onChange={(e) => set('name')(e.target.value)}
          maxLength={NAME_MAX}
          placeholder="Association name"
          aria-label="Name"
          className="mt-2 w-full border-0 bg-transparent p-0 text-xl font-semibold text-fg placeholder:text-fg-faint focus:outline-none focus:ring-0"
        />
        <textarea
          value={draft.description}
          onChange={(e) => set('description')(e.target.value)}
          maxLength={DESCRIPTION_MAX}
          rows={2}
          placeholder="Add a description — the community, its size, anything to know."
          aria-label="Description"
          className="mt-3 w-full resize-none border-0 bg-transparent p-0 text-[15px]/6 text-fg-secondary placeholder:text-fg-faint focus:outline-none focus:ring-0"
        />

        <div className="mt-2 flex flex-wrap items-center gap-1 border-t border-line/60 pt-3">
          <PropertyPicker
            options={companies.map((c) => ({ value: c.id, label: c.name }))}
            value={draft.companyId}
            onChange={set('companyId')}
            placeholder="Company…"
            ariaLabel={
              company
                ? `Company: ${company.name}`
                : isSuperAdmin
                  ? 'Choose company'
                  : 'Company: yours'
            }
            readOnly={!isSuperAdmin || companiesLoading}
            trigger={
              <>
                <BuildingOffice2Icon className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
                {company ? (
                  company.name
                ) : (
                  <span className="text-fg-muted">
                    {!isSuperAdmin
                      ? 'Your company'
                      : companiesLoading
                        ? 'Loading companies…'
                        : 'Company'}
                  </span>
                )}
              </>
            }
          />
          <PropertyPicker
            options={usStates.map((s) => ({ value: s.value, label: s.label }))}
            value={draft.state}
            onChange={set('state')}
            placeholder="State…"
            ariaLabel={state ? `State: ${state.label}` : 'Choose state'}
            trigger={
              <>
                <MapPinIcon className="h-3.5 w-3.5 text-fg-muted" aria-hidden />
                {state ? state.label : <span className="text-fg-muted">State</span>}
              </>
            }
          />
          <PropertyPicker
            options={[
              ...(mustChooseZone
                ? []
                : [{ value: STATE_DEFAULT, label: 'The state’s default' }]),
              ...US_TIMEZONES.map((z) => ({ value: z.id, label: `${z.label} (${z.id})` })),
            ]}
            value={draft.timezone ?? (mustChooseZone ? null : STATE_DEFAULT)}
            onChange={(v) => set('timezone')(v === STATE_DEFAULT ? null : v)}
            placeholder="Timezone…"
            ariaLabel={zone ? `Timezone: ${zone.label}` : 'Choose timezone'}
            trigger={
              <>
                <ClockIcon
                  className={clsx(
                    'h-3.5 w-3.5',
                    mustChooseZone && !zone ? 'text-danger' : 'text-fg-muted'
                  )}
                  aria-hidden
                />
                {zone ? (
                  zone.label
                ) : (
                  <span className="text-fg-muted">
                    {mustChooseZone ? 'Timezone' : 'State’s timezone'}
                  </span>
                )}
              </>
            }
          />
        </div>
        {mustChooseZone && state && !zone && (
          <p className="mt-2 text-[12px] text-fg-muted">
            {state.label} spans {SPLIT_ZONE_STATES[state.value]}. Choose the one
            this association keeps: due dates and late fees follow its calendar
            day.
          </p>
        )}

        <fieldset className="mt-4">
          <legend className="text-[12px] font-medium text-fg-secondary">Address</legend>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-6">
            {field('street', 'Street', { autoComplete: 'address-line1' }, 'sm:col-span-4')}
            {field('street2', 'Unit, suite', { autoComplete: 'address-line2' }, 'sm:col-span-2')}
            {field('city', 'City', { autoComplete: 'address-level2' }, 'sm:col-span-4')}
            {field(
              'zip',
              'ZIP code',
              { inputMode: 'numeric', autoComplete: 'postal-code', placeholder: '12345' },
              'sm:col-span-2'
            )}
          </div>
        </fieldset>

        <fieldset className="mt-4">
          <legend className="text-[12px] font-medium text-fg-secondary">Contact</legend>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {field('phone', 'Phone', {
              type: 'tel',
              autoComplete: 'tel',
              onBlur: () => {
                touch('phone')();
                set('phone')(formatPhoneNumber(draft.phone));
              },
            })}
            {field('email', 'Email', { type: 'email', autoComplete: 'email' })}
            {field(
              'website',
              'Website (optional)',
              { type: 'url', placeholder: 'https://' },
              'sm:col-span-2'
            )}
          </div>
        </fieldset>

        <ErrorAlert className="mt-3">{error}</ErrorAlert>

        <div className="mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-line/60 pt-4">
          {missing.length > 0 && (draft.name || draft.street || draft.city) && (
            <p className="mr-auto text-[12px] text-fg-muted">
              Still needs {missing.join(', ')}.
            </p>
          )}
          <Button plain type="button" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            color="sky"
            type="submit"
            disabled={!canSubmit}
            title={missing.length ? `Add ${missing.join(', ')}` : undefined}
          >
            {saving ? 'Creating…' : 'Create association'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};
